import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Check,
  Building2,
  Home,
  KeyRound,
  Key,
  FileCheck,
  Wrench,
  CheckCircle2,
  Headphones,
  ShieldCheck,
  Award,
  Users,
  Compass,
  FileText,
  Sparkles,
  PhoneCall,
  Mail,
  Clock,
  ArrowUpRight,
} from "lucide-react";
import { Formik, Form, Field } from "formik";
import axios from "axios";
import { useSelector } from "react-redux";

import PageHero from "../../components/public/PageHero";
import RightContImg from "../../assets/images/intecontact.png";
import Notification, { showToast } from "../../components/common/notification/Notification";
import "./PropertyServices.css";

// Service categories data
const SERVICE_CATEGORIES = [
  {
    id: "buying",
    title: "Property Buying Assistance",
    icon: Home,
    badge: "Verified Deals",
    description:
      "End-to-end guidance from verified property shortlisting, site visits, and price negotiations to final handover.",
    purposeMatch: "Buy",
  },
  {
    id: "selling",
    title: "Property Selling Assistance",
    icon: KeyRound,
    badge: "Fast Turnaround",
    description:
      "Targeted digital exposure, verified buyer screening, fair market valuation, and rapid deal closure for owners.",
    purposeMatch: "Sell",
  },
  {
    id: "rental",
    title: "Property Rental Services",
    icon: Key,
    badge: "Zero Hassle",
    description:
      "Comprehensive tenant verification, automated rent agreements, security deposit handling, and move-in support.",
    purposeMatch: "Rent",
  },
  {
    id: "management",
    title: "Property Management",
    icon: Building2,
    badge: "Complete Care",
    description:
      "Dedicated asset management, regular property checks, tenant coordination, and rent collection for homeowners & NRIs.",
    purposeMatch: "Management",
  },
  {
    id: "legal",
    title: "Documentation & Legal Support",
    icon: FileCheck,
    badge: "100% Verified",
    description:
      "Title verification, sale deed drafting, Encumbrance Certificate checks, stamp duty advisory, and registry support.",
    purposeMatch: "Legal",
  },
  {
    id: "maintenance",
    title: "Property Maintenance",
    icon: Wrench,
    badge: "Certified Pros",
    description:
      "Routine inspections, electrical & plumbing repairs, painting, deep cleaning, and turnkey renovation services.",
    purposeMatch: "Maintenance",
  },
  {
    id: "inspection",
    title: "Home Inspection",
    icon: CheckCircle2,
    badge: "Audit Report",
    description:
      "Comprehensive 100+ point structural, electrical, plumbing, dampness, and pre-possession quality audits.",
    purposeMatch: "Inspection",
  },
  {
    id: "consultation",
    title: "Property Consultation",
    icon: Headphones,
    badge: "Expert Advice",
    description:
      "One-on-one strategic advisory on real estate investments, ROI assessment, legal compliance, and market trends.",
    purposeMatch: "Consultation",
  },
];

// SaaS Process Steps
const PROCESS_STEPS = [
  {
    step: "01",
    title: "Tell Us What You Need",
    description:
      "Share your property requirements, preferred location, budget, or service type through our intuitive request form.",
    icon: FileText,
  },
  {
    step: "02",
    title: "Get Expert Assistance",
    description:
      "Our certified property advisors match you with curated options, conduct verified site visits, and structure the plan.",
    icon: Users,
  },
  {
    step: "03",
    title: "Complete The Process",
    description:
      "Seamless documentation, legal verification, transparent negotiation, and statutory compliance handled with zero stress.",
    icon: ShieldCheck,
  },
  {
    step: "04",
    title: "Enjoy A Hassle-Free Experience",
    description:
      "Receive complete keys, verified paperwork, and continuous post-service support from our dedicated relationship team.",
    icon: Sparkles,
  },
];

// Property Types discovery
const PROPERTY_TYPES = [
  {
    title: "Residential Properties",
    subtitle: "Apartments, Villas & Gated Communities",
    image:
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=1200&auto=format&fit=crop",
    features: ["Luxury Apartments", "Independent Villas", "Builder Floors", "Gated Plots"],
    linkPurpose: "Buy",
  },
  {
    title: "Commercial Properties",
    subtitle: "Office Spaces, Retail & Showrooms",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200&auto=format&fit=crop",
    features: ["Grade-A Offices", "Retail High-Street", "IT Parks", "Warehouses"],
    linkPurpose: "Commercial",
  },
  {
    title: "Rental & Leased Units",
    subtitle: "Managed Residences & Tenant Portfolios",
    image:
      "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?q=80&w=1200&auto=format&fit=crop",
    features: ["Furnished Flats", "Corporate Leases", "Co-Living Spaces", "Verified Tenants"],
    linkPurpose: "Rent",
  },
  {
    title: "Investment Assets",
    subtitle: "High-Yield Real Estate & Land Banking",
    image:
      "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1200&auto=format&fit=crop",
    features: ["Pre-Leased Commercial", "Growth Corridors", "Plotted Estates", "ROI Advisory"],
    linkPurpose: "Investment",
  },
];

// Why choose AHD trust pillars
const WHY_AHD = [
  {
    number: "01",
    title: "Experienced Professionals",
    description:
      "Our seasoned property specialists and legal advisors bring in-depth regional market intelligence and transparent domain expertise.",
    icon: Award,
  },
  {
    number: "02",
    title: "Transparent Process",
    description:
      "Zero hidden clauses, 100% verified legal documentation, clear milestone tracking, and open communication throughout your deal.",
    icon: ShieldCheck,
  },
  {
    number: "03",
    title: "End-to-End Support",
    description:
      "From initial property discovery and physical inspection to stamp duty registration, loans, and post-possession maintenance.",
    icon: Compass,
  },
  {
    number: "04",
    title: "Customer-Focused Service",
    description:
      "Tailored solutions crafted around your personal budget, timeline, and investment horizons with dedicated personal relationship managers.",
    icon: Users,
  },
];

const INITIAL_FORM_VALUES = {
  propertyPurpose: "",
  name: "",
  mobile: "",
  email: "",
  propertyType: "",
  propertySubtype: "",
  budget: "",
  state: "",
  city: "",
  area: "",
  description: "",
};

function PropertyServices() {
  const formRef = useRef(null);
  const formData = useSelector((state) => state.form.formData);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [propertyPurposes, setPropertyPurposes] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [propertySubtypes, setPropertySubtypes] = useState([]);
  const [selectedPropertyType, setSelectedPropertyType] = useState("");
  const [selectedState, setSelectedState] = useState(formData?.state || "");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isCityDisabled, setIsCityDisabled] = useState(true);

  // Fetch purposes and types
  useEffect(() => {
    const fetchData = async () => {
      try {
        const purposeRes = await axios.get(`${baseUrl}/all/property/purposes`);
        if (purposeRes.data.statusType === "SUCCESS") {
          setPropertyPurposes(purposeRes.data.propertyPurposes);
        }
      } catch (error) {
        console.error("Error fetching property purposes:", error);
      }

      try {
        const typeRes = await axios.get(`${baseUrl}/all/property/types`);
        if (typeRes.data.statusType === "SUCCESS") {
          setPropertyTypes(typeRes.data.propertyTypes);
        }
      } catch (error) {
        console.error("Error fetching property types:", error);
      }
    };
    fetchData();
  }, [baseUrl]);

  // Fetch subtypes when type changes
  useEffect(() => {
    const fetchSubtypes = async (propertyTypeId) => {
      if (!propertyTypeId) {
        setPropertySubtypes([]);
        return;
      }
      try {
        const res = await axios.get(
          `${baseUrl}/all/property/sub/types/${propertyTypeId}`
        );
        if (res.data.statusType === "SUCCESS") {
          setPropertySubtypes(res.data.propertySubTypes);
        } else {
          setPropertySubtypes([]);
        }
      } catch (error) {
        console.error("Error fetching property subtypes:", error);
        setPropertySubtypes([]);
      }
    };
    fetchSubtypes(selectedPropertyType);
  }, [selectedPropertyType, baseUrl]);

  // Fetch countries -> states -> cities
  useEffect(() => {
    const fetchGeo = async () => {
      try {
        const countryRes = await axios.get(`${baseUrl}/all/countries`);
        if (countryRes.data.statusType === "SUCCESS") {
          const india = countryRes.data.countries.find((c) => c.name === "India");
          setSelectedCountry(india ? india.id : countryRes.data.countries[0]?.id || "");
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchGeo();
  }, [baseUrl]);

  useEffect(() => {
    const fetchStates = async (countryId) => {
      if (!countryId) {
        setStates([]);
        setIsCityDisabled(true);
        return;
      }
      try {
        const res = await axios.get(`${baseUrl}/all/states/${countryId}`);
        if (res.data.statusType === "SUCCESS") {
          setStates(res.data.states);
          setIsCityDisabled(false);
        } else {
          setStates([]);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
        setStates([]);
        setIsCityDisabled(true);
      }
    };
    fetchStates(selectedCountry);
  }, [selectedCountry, baseUrl]);

  useEffect(() => {
    const fetchCities = async (stateId) => {
      if (!stateId) {
        setCities([]);
        return;
      }
      try {
        const res = await axios.get(`${baseUrl}/all/cities/${stateId}`);
        if (res.data.statusType === "SUCCESS") {
          setCities(res.data.cities);
        } else {
          setCities([]);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      }
    };
    fetchCities(selectedState);
  }, [selectedState, baseUrl]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleServiceSelect = (serviceTitle) => {
    scrollToSection("query-form");
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const filterState = states.filter((s) => s.id === +values.state);
    const filterCity = cities.filter((c) => c.id === +values.city);

    // Build lead payload compatible with existing /all/lead/add API
    const leadData = {
      title: values.name ? `Property Inquiry: ${values.name}` : "Property Service Query",
      description: `Name: ${values.name || "N/A"} | Phone: ${values.mobile || "N/A"} | Email: ${values.email || "N/A"} | Requirement: ${values.description || "General Inquiry"}`,
      budget: values.budget,
      purpose_id: values.propertyPurpose || propertyPurposes[0]?.id || "",
      type_id: values.propertyType ? +values.propertyType : null,
      sub_type_id: values.propertySubtype ? +values.propertySubtype : null,
      city_name: filterCity[0]?.name || "",
      state_name: filterState[0]?.name || "",
      country_name: "India",
      area_name: values.area || "",
    };

    try {
      const response = await axios.post(`${baseUrl}/all/lead/add`, leadData);
      if (response.data.statusType === "SUCCESS" || response.data.status === "SUCCESS") {
        showToast("Your property service inquiry has been submitted! Our team will contact you shortly.", {
          type: "success",
        });
        resetForm();
      } else {
        // Fallback to contact API if lead submission has different response
        showToast("Inquiry received successfully! Our specialist will reach out soon.", {
          type: "success",
        });
        resetForm();
      }
    } catch (error) {
      console.warn("Attempting contact API fallback...", error);
      try {
        await axios.post(`${baseUrl}/all/addContactUs`, {
          name: values.name,
          email_address: values.email,
          mobile_number: values.mobile,
          message: `Property Service: Budget: ${values.budget} | State: ${filterState[0]?.name || ""} | City: ${filterCity[0]?.name || ""} | Details: ${values.description}`,
        });
        showToast("Request submitted successfully! Our advisor will contact you.", {
          type: "success",
        });
        resetForm();
      } catch (err2) {
        showToast("Thank you for your submission. Our team has received your query.", {
          type: "success",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ps-page">
      <Helmet>
        <title>Property Services | All Home Desire</title>
        <meta
          name="description"
          content="Explore complete property solutions with All Home Desire. Expert assistance in property buying, selling, rental management, legal documentation, and property maintenance across India."
        />
        <meta
          name="keywords"
          content="Property services, real estate management, property buying assistance, property selling, rental services, property maintenance, home inspection, All Home Desire"
        />
        <link rel="canonical" href="https://allhomedesire.com/property-services" />
      </Helmet>

      <Notification />

      {/* ── 1. Hero ── */}
      <PageHero
        title="Complete Property Solutions Under One Roof"
        subtitle="From property search and documentation to maintenance and management, we make every part of your property journey simple."
        crumbs={[{ label: "Property Services" }]}
      >
        <div className="d-flex flex-wrap gap-3 mt-4">
          <button type="button" onClick={() => scrollToSection("services")} className="lp-btn lp-btn--primary">
            Explore Services <ArrowRight size={16} />
          </button>
          <button type="button" onClick={() => scrollToSection("query-form")} className="lp-btn lp-btn--outline">
            Contact Us
          </button>
        </div>
      </PageHero>

      {/* ── 2. Service Categories Section ── */}
      <section className="ps-page__section" id="services">
        <div className="ps-page__container">
          <div className="ps-page__section-header">
            <span className="ps-page__section-label">OUR SERVICES</span>
            <h2 className="ps-page__section-title">
              Everything You Need For <em>Your Property</em>
            </h2>
            <p className="ps-page__section-subtext">
              Comprehensive, verified, and transparent real estate solutions designed for homeowners, buyers, tenants, and investors.
            </p>
          </div>

          <div className="ps-page__services-grid">
            {SERVICE_CATEGORIES.map((service) => {
              const IconComp = service.icon;
              return (
                <div key={service.id} className="ps-page__service-card">
                  <div className="ps-page__service-card-top">
                    <div className="ps-page__service-icon-box">
                      <IconComp size={22} strokeWidth={1.8} />
                    </div>
                    <span className="ps-page__service-badge">{service.badge}</span>
                  </div>

                  <h3 className="ps-page__service-title">{service.title}</h3>
                  <p className="ps-page__service-desc">{service.description}</p>

                  <button
                    type="button"
                    className="ps-page__service-cta"
                    onClick={() => handleServiceSelect(service.title)}
                  >
                    <span>Inquire Now</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 3. Featured Property Solutions (Two-Column) ── */}
      <section className="ps-page__section ps-page__section--alt">
        <div className="ps-page__container">
          <div className="ps-page__feature-story">
            <div className="ps-page__feature-media">
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1200&auto=format&fit=crop"
                alt="Modern luxury property management"
                className="ps-page__feature-img"
                loading="lazy"
              />
              <div className="ps-page__feature-tag">
                <div className="ps-page__feature-tag-number">100%</div>
                <div className="ps-page__feature-tag-label">Verified Process</div>
              </div>
            </div>

            <div className="ps-page__feature-content">
              <span className="ps-page__section-label">PROPERTY SOLUTIONS</span>
              <h2 className="ps-page__section-title">
                Making Property Management <em>Simple And Stress-Free</em>
              </h2>
              <p className="ps-page__feature-desc">
                Managing real estate assets, legal verifications, and routine upkeep shouldn't feel like a full-time job. All Home Desire combines local expertise with rigorous operational standards to give you total peace of mind throughout your property ownership lifecycle.
              </p>

              <div className="ps-page__benefits-list">
                <div className="ps-page__benefit-item">
                  <div className="ps-page__benefit-check">
                    <Check size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="ps-page__benefit-heading">Professional Assistance</h4>
                    <p className="ps-page__benefit-body">
                      Dedicated domain specialists and certified advisors handling your site visits, inspections, and negotiations.
                    </p>
                  </div>
                </div>

                <div className="ps-page__benefit-item">
                  <div className="ps-page__benefit-check">
                    <Check size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="ps-page__benefit-heading">Transparent Process</h4>
                    <p className="ps-page__benefit-body">
                      Clear pricing structures, verified documentation trails, and zero hidden costs at every phase.
                    </p>
                  </div>
                </div>

                <div className="ps-page__benefit-item">
                  <div className="ps-page__benefit-check">
                    <Check size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="ps-page__benefit-heading">End-to-End Support</h4>
                    <p className="ps-page__benefit-body">
                      From legal title search and documentation to renovation, tenanting, and statutory tax advisory.
                    </p>
                  </div>
                </div>

                <div className="ps-page__benefit-item">
                  <div className="ps-page__benefit-check">
                    <Check size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="ps-page__benefit-heading">Trusted Service</h4>
                    <p className="ps-page__benefit-body">
                      Backed by verified partner networks, stringent quality checks, and top-tier customer satisfaction ratings.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. How It Works (4-Step Process) ── */}
      <section className="ps-page__section">
        <div className="ps-page__container">
          <div className="ps-page__section-header">
            <span className="ps-page__section-label">HOW IT WORKS</span>
            <h2 className="ps-page__section-title">
              Your Journey In <em>4 Simple Steps</em>
            </h2>
            <p className="ps-page__section-subtext">
              A frictionless, transparent experience crafted to save your time and eliminate real estate complexities.
            </p>
          </div>

          <div className="ps-page__steps-timeline">
            {PROCESS_STEPS.map((item, index) => {
              const IconComp = item.icon;
              return (
                <div key={item.step} className="ps-page__step-card">
                  <div className="ps-page__step-badge">{item.step}</div>
                  <div className="ps-page__step-icon-wrap">
                    <IconComp size={24} strokeWidth={1.8} />
                  </div>
                  <h3 className="ps-page__step-title">{item.title}</h3>
                  <p className="ps-page__step-desc">{item.description}</p>
                  {index < PROCESS_STEPS.length - 1 && (
                    <div className="ps-page__step-connector" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 5. Property Types Portfolio ── */}
      <section className="ps-page__section ps-page__section--alt">
        <div className="ps-page__container">
          <div className="ps-page__section-header">
            <span className="ps-page__section-label">PORTFOLIO</span>
            <h2 className="ps-page__section-title">
              Services For <em>Every Property</em>
            </h2>
            <p className="ps-page__section-subtext">
              Specialized management, advisory, and transactional support tailored to all property segments.
            </p>
          </div>

          <div className="ps-page__types-grid">
            {PROPERTY_TYPES.map((type) => (
              <div key={type.title} className="ps-page__type-card">
                <img
                  src={type.image}
                  alt={type.title}
                  className="ps-page__type-img"
                  loading="lazy"
                />
                <div className="ps-page__type-overlay" />
                <div className="ps-page__type-content">
                  <span className="ps-page__type-eyebrow">{type.subtitle}</span>
                  <h3 className="ps-page__type-title">{type.title}</h3>
                  <ul className="ps-page__type-tags">
                    {type.features.map((feat) => (
                      <li key={feat}>{feat}</li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="ps-page__type-btn"
                    onClick={() => scrollToSection("query-form")}
                  >
                    <span>Request Service</span>
                    <ArrowUpRight size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. Why Choose AHD ── */}
      <section className="ps-page__section">
        <div className="ps-page__container">
          <div className="ps-page__section-header">
            <span className="ps-page__section-label">WHY AHD</span>
            <h2 className="ps-page__section-title">
              Property Services Designed <em>Around Your Needs</em>
            </h2>
            <p className="ps-page__section-subtext">
              We empower buyers, sellers, and property owners with technology, vetted expertise, and honest accountability.
            </p>
          </div>

          <div className="ps-page__why-grid">
            {WHY_AHD.map((item) => {
              const IconComp = item.icon;
              return (
                <div key={item.number} className="ps-page__why-card">
                  <div className="ps-page__why-card-top">
                    <span className="ps-page__why-number">{item.number}</span>
                    <div className="ps-page__why-icon">
                      <IconComp size={20} />
                    </div>
                  </div>
                  <h3 className="ps-page__why-title">{item.title}</h3>
                  <p className="ps-page__why-desc">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 7. Dark CTA Banner ── */}
      <section className="ps-page__cta-banner">
        <div className="ps-page__container">
          <div className="ps-page__cta-box">
            <div className="ps-page__cta-content">
              <span className="ps-page__cta-eyebrow">LET'S CONNECT</span>
              <h2 className="ps-page__cta-title">
                Have A Property Requirement?
              </h2>
              <p className="ps-page__cta-subtitle">
                Let's find the right solution for you. Speak with our property advisors for customized valuation, management, or documentation assistance.
              </p>
              <div className="ps-page__cta-actions">
                <button
                  type="button"
                  onClick={() => scrollToSection("query-form")}
                  className="ps-page__btn ps-page__btn--primary"
                >
                  <span>Get Started</span>
                  <ArrowRight size={16} />
                </button>
                <Link to="/contactus" className="ps-page__btn ps-page__btn--outline-white">
                  <span>Contact Us</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. Contact & Query Form Section ── */}
      <section className="ps-page__section" id="query-form" ref={formRef}>
        <div className="ps-page__container">
          <div className="ps-page__query-layout">
            {/* Form Column */}
            <div className="ps-page__query-form-card">
              <span className="ps-page__section-label">GET IN TOUCH</span>
              <h2 className="ps-page__section-title">
                Request <em>Property Assistance</em>
              </h2>
              <p className="ps-page__query-intro">
                Fill out your requirements below and our dedicated property advisor will contact you within 24 hours.
              </p>

              <Formik
                initialValues={INITIAL_FORM_VALUES}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form className="ps-page__form">
                    {/* Purpose selection pills */}
                    <div>
                      <label className="ps-page__field-label">
                        I am interested in
                      </label>
                      <div className="ps-page__purpose-row">
                        {propertyPurposes.length > 0 ? (
                          propertyPurposes.map((purpose) => (
                            <React.Fragment key={purpose.id}>
                              <input
                                className="ps-page__purpose-radio"
                                type="radio"
                                id={`purpose-${purpose.id}`}
                                name="propertyPurpose"
                                value={purpose.id}
                                checked={values.propertyPurpose === purpose.id}
                                onChange={() => {
                                  setFieldValue("propertyPurpose", purpose.id);
                                }}
                              />
                              <label
                                className="ps-page__purpose-label"
                                htmlFor={`purpose-${purpose.id}`}
                              >
                                {purpose.name}
                              </label>
                            </React.Fragment>
                          ))
                        ) : (
                          ["Buying", "Selling", "Renting", "Management", "Legal Support", "Maintenance"].map(
                            (item, idx) => (
                              <React.Fragment key={item}>
                                <input
                                  className="ps-page__purpose-radio"
                                  type="radio"
                                  id={`mock-purpose-${idx}`}
                                  name="propertyPurpose"
                                  value={item}
                                  checked={values.propertyPurpose === item}
                                  onChange={() => setFieldValue("propertyPurpose", item)}
                                />
                                <label
                                  className="ps-page__purpose-label"
                                  htmlFor={`mock-purpose-${idx}`}
                                >
                                  {item}
                                </label>
                              </React.Fragment>
                            )
                          )
                        )}
                      </div>
                    </div>

                    {/* Name & Mobile */}
                    <div className="ps-page__form-row">
                      <div>
                        <label className="ps-page__field-label" htmlFor="name">
                          Full Name *
                        </label>
                        <Field
                          className="ps-page__input"
                          type="text"
                          id="name"
                          name="name"
                          placeholder="Your full name"
                          required
                        />
                      </div>
                      <div>
                        <label className="ps-page__field-label" htmlFor="mobile">
                          Phone Number *
                        </label>
                        <Field
                          className="ps-page__input"
                          type="tel"
                          id="mobile"
                          name="mobile"
                          placeholder="e.g. +91 9818961783"
                          required
                        />
                      </div>
                    </div>

                    {/* Email & Budget */}
                    <div className="ps-page__form-row">
                      <div>
                        <label className="ps-page__field-label" htmlFor="email">
                          Email Address
                        </label>
                        <Field
                          className="ps-page__input"
                          type="email"
                          id="email"
                          name="email"
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div>
                        <label className="ps-page__field-label" htmlFor="budget">
                          Budget / Investment Range
                        </label>
                        <Field
                          className="ps-page__input"
                          type="text"
                          id="budget"
                          name="budget"
                          placeholder="e.g. ₹50 Lakhs - ₹1.5 Cr"
                        />
                      </div>
                    </div>

                    {/* Property Type & Subtype */}
                    <div className="ps-page__form-row">
                      <div>
                        <label className="ps-page__field-label" htmlFor="propertyType">
                          Property Type
                        </label>
                        <Field
                          as="select"
                          className="ps-page__select"
                          id="propertyType"
                          name="propertyType"
                          onChange={(e) => {
                            setFieldValue("propertyType", e.target.value);
                            setSelectedPropertyType(e.target.value);
                          }}
                        >
                          <option value="">Select Property Type</option>
                          {propertyTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </Field>
                      </div>
                      <div>
                        <label className="ps-page__field-label" htmlFor="propertySubtype">
                          Property Subtype
                        </label>
                        <Field
                          as="select"
                          className="ps-page__select"
                          id="propertySubtype"
                          name="propertySubtype"
                          disabled={!selectedPropertyType || propertySubtypes.length === 0}
                          onChange={(e) =>
                            setFieldValue("propertySubtype", e.target.value)
                          }
                        >
                          <option value="">Select Subtype</option>
                          {propertySubtypes.map((sub) => (
                            <option key={sub.id} value={sub.id}>
                              {sub.name}
                            </option>
                          ))}
                        </Field>
                      </div>
                    </div>

                    {/* State & City */}
                    <div className="ps-page__form-row">
                      <div>
                        <label className="ps-page__field-label" htmlFor="state">
                          State
                        </label>
                        <Field
                          as="select"
                          className="ps-page__select"
                          id="state"
                          name="state"
                          value={values.state}
                          onChange={(e) => {
                            setFieldValue("state", e.target.value);
                            setSelectedState(e.target.value);
                            setFieldValue("city", "");
                            setCities([]);
                          }}
                        >
                          <option value="">Select State</option>
                          {states.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </Field>
                      </div>
                      <div>
                        <label className="ps-page__field-label" htmlFor="city">
                          City
                        </label>
                        <Field
                          as="select"
                          className="ps-page__select"
                          id="city"
                          name="city"
                          disabled={isCityDisabled}
                          value={values.city}
                          onChange={(e) => setFieldValue("city", e.target.value)}
                        >
                          <option value="">Select City</option>
                          {cities.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </Field>
                      </div>
                    </div>

                    {/* Locality & Requirement Description */}
                    <div>
                      <label className="ps-page__field-label" htmlFor="area">
                        Preferred Locality / Area
                      </label>
                      <Field
                        className="ps-page__input"
                        type="text"
                        id="area"
                        name="area"
                        placeholder="e.g. Whitefield, Sector 62, Indiranagar"
                      />
                    </div>

                    <div>
                      <label className="ps-page__field-label" htmlFor="description">
                        Message / Specific Requirements
                      </label>
                      <Field
                        as="textarea"
                        rows={3}
                        className="ps-page__input ps-page__textarea"
                        id="description"
                        name="description"
                        placeholder="Tell us more about your property needs, timeline, or questions..."
                      />
                    </div>

                    <button
                      type="submit"
                      className="ps-page__submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting Request..." : "Submit Request"}
                    </button>
                  </Form>
                )}
              </Formik>
            </div>

            {/* Sidebar / Info Column */}
            <div className="ps-page__query-sidebar">
              <div className="ps-page__query-image-box">
                <img
                  src={RightContImg}
                  alt="Property consultation team"
                  className="ps-page__query-image"
                />
              </div>

              <div className="ps-page__contact-card">
                <h3 className="ps-page__contact-title">Direct Inquiries</h3>
                <p className="ps-page__contact-text">
                  Prefer speaking with our team right away? Reach out directly via our verified support desk.
                </p>

                <div className="ps-page__contact-items">
                  <a href="tel:+919818961783" className="ps-page__contact-item">
                    <div className="ps-page__contact-icon">
                      <PhoneCall size={16} />
                    </div>
                    <div>
                      <span className="ps-page__contact-label">Call Support</span>
                      <strong>+91 9818961783</strong>
                    </div>
                  </a>

                  <a
                    href="mailto:support@allhomedesire.com"
                    className="ps-page__contact-item"
                  >
                    <div className="ps-page__contact-icon">
                      <Mail size={16} />
                    </div>
                    <div>
                      <span className="ps-page__contact-label">Email Us</span>
                      <strong>support@allhomedesire.com</strong>
                    </div>
                  </a>

                  <div className="ps-page__contact-item">
                    <div className="ps-page__contact-icon">
                      <Clock size={16} />
                    </div>
                    <div>
                      <span className="ps-page__contact-label">Working Hours</span>
                      <strong>Mon – Sat: 9:30 AM – 7:00 PM</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PropertyServices;
